import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';
import { IncomeAuditService } from '../services/income-audit.service';
import {
  AuditScreeningReviewResponse,
  AuditScreeningSummary,
  AuditDraftDetail,
  AuditIssueDetail,
} from '../models/income-audit.models';
import { IncomeStreamType, SopCheckResult } from 'src/app/models/Argyle';
import { parseDraftDetail, DocUploadDetail, WorkNumberDetail } from 'src/app/models/income-wizard-workflow';

export interface AuditFlag {
  severity: 'info' | 'warn' | 'danger';
  icon: string;
  message: string;
}

@Component({
  selector: 'app-audit-review-modal',
  templateUrl: './audit-review-modal.component.html',
  styleUrls: ['./audit-review-modal.component.less'],
})
export class AuditReviewModalComponent implements OnInit {
  @Input() screeningId!: number;

  loading = true;
  error: string | null = null;
  summary: AuditScreeningSummary | null = null;
  drafts: AuditDraftDetail[] = [];
  issues: AuditIssueDetail[] = [];
  flags: AuditFlag[] = [];

  // Mark audited state
  selectedRating: string | null = null;
  auditNotes = '';
  markingAudited = false;
  markError: string | null = null;

  constructor(
    public activeModal: NgbActiveModal,
    private auditService: IncomeAuditService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.auditService.getScreeningReview(this.screeningId).subscribe({
      next: (res) => {
        this.summary = res.summary;
        this.drafts = res.drafts ?? [];
        this.issues = res.issues ?? [];
        this.flags = this.computeFlags();
        this.loading = false;
      },
      error: () => {
        this.error = 'Failed to load screening review.';
        this.loading = false;
      },
    });
  }

  get includedDrafts(): AuditDraftDetail[] {
    return this.drafts.filter((d) => !d.isExcluded);
  }

  get excludedDrafts(): AuditDraftDetail[] {
    return this.drafts.filter((d) => d.isExcluded);
  }

  get totalVerified(): number {
    return this.includedDrafts.reduce((sum, d) => sum + d.incomeAmount, 0);
  }

  get totalSuggested(): number {
    return this.includedDrafts.reduce((sum, d) => sum + d.suggestedAmount, 0);
  }

  get alignmentRatio(): number | null {
    if (!this.summary?.suppliedIncome || this.summary.suppliedIncome <= 0) return null;
    return Math.round((this.totalVerified / this.summary.suppliedIncome) * 1000) / 10;
  }

  get isAligned(): boolean | null {
    if (this.alignmentRatio == null) return null;
    return this.alignmentRatio >= 95;
  }

  get overallDelta(): number | null {
    if (this.totalSuggested <= 0) return null;
    return Math.round(((this.totalVerified - this.totalSuggested) / this.totalSuggested) * 1000) / 10;
  }

  get dangerFlags(): AuditFlag[] { return this.flags.filter((f) => f.severity === 'danger'); }
  get warnFlags(): AuditFlag[] { return this.flags.filter((f) => f.severity === 'warn'); }
  get infoFlags(): AuditFlag[] { return this.flags.filter((f) => f.severity === 'info'); }

  streamTypeLabel(type: number): string {
    switch (type) {
      case IncomeStreamType.WorkNumber: return 'Work Number';
      case IncomeStreamType.PayrollSync: return 'Payroll';
      case IncomeStreamType.DocUpload: return 'Doc Upload';
      case IncomeStreamType.BankSync: return 'Bank Sync';
      default: return 'Unknown';
    }
  }

  formatDuration(seconds: number | null): string {
    if (!seconds || seconds <= 0) return '—';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  }

  openFullReview(): void {
    window.open(`/screenings/review/${this.screeningId}`, '_blank');
  }

  markAudited(): void {
    if (!this.selectedRating || this.markingAudited) return;
    this.markingAudited = true;
    this.markError = null;

    this.auditService.markScreeningAudited(this.screeningId, this.selectedRating, this.auditNotes.trim() || undefined).subscribe({
      next: () => {
        this.markingAudited = false;
        this.activeModal.close('audited');
      },
      error: () => {
        this.markingAudited = false;
        this.markError = 'Failed to save audit review.';
      },
    });
  }

  private computeFlags(): AuditFlag[] {
    const flags: AuditFlag[] = [];
    const applicantName = (this.summary?.applicantName ?? '').toLowerCase().trim();

    // 1. Alignment check (SOP Phase 2)
    if (this.summary?.suppliedIncome && this.summary.suppliedIncome > 0) {
      const ratio = this.totalSuggested / this.summary.suppliedIncome;
      if (ratio < 0.95) {
        flags.push({
          severity: 'warn',
          icon: '📊',
          message: `Income does not meet 95% alignment threshold (${Math.round(ratio * 100)}%)`,
        });
      }
    }

    for (const d of this.drafts) {
      const employer = d.employerName || 'Unknown';

      // 2. Delta check (SOP Phase 4 — monthly calc accuracy)
      if (!d.isExcluded && d.deltaPercent != null && (d.deltaPercent < -5 || d.deltaPercent > 5)) {
        const severity = d.deltaPercent < -15 || d.deltaPercent > 15 ? 'danger' : 'warn';
        flags.push({
          severity,
          icon: '📐',
          message: `Processor deviated ${d.deltaPercent}% from system suggestion on ${employer}`,
        });
      }

      // 3. Fraud signals (SOP Phase 3 — doc review)
      if (d.detailJson) {
        const detail = this.parseDocDetail(d);
        if (detail) {
          if (detail.authenticityStatus === 'failed') {
            flags.push({
              severity: 'danger',
              icon: '🚨',
              message: `Authenticity failed on ${employer} (score: ${detail.authenticityScore})`,
            });
          }
          if (detail.fraudSignals && detail.fraudSignals.length > 0) {
            for (const sig of detail.fraudSignals) {
              flags.push({
                severity: sig.confidence === 'HIGH' ? 'danger' : 'warn',
                icon: '🚨',
                message: `${sig.description} on ${employer} (${sig.confidence})`,
              });
            }
          }

          // 5. Name mismatch (SOP Phase 3 — identity validation)
          if (detail.recipientName && applicantName) {
            const docName = detail.recipientName.toLowerCase().trim();
            if (docName && docName !== applicantName) {
              flags.push({
                severity: 'warn',
                icon: '👤',
                message: `Name mismatch on ${employer}: "${detail.recipientName}" vs "${this.summary?.applicantName}"`,
              });
            }
          }
        }
      }

      // 4. Speed check — suspiciously fast review
      if (d.reviewDurationSeconds != null && d.reviewDurationSeconds > 0 && d.reviewDurationSeconds < 10) {
        flags.push({
          severity: 'warn',
          icon: '⏱️',
          message: `${employer} reviewed in ${d.reviewDurationSeconds}s`,
        });
      }

      // 6. SOP checks from detailJson (RIQ-6206)
      if (d.detailJson) {
        const parsed = d.streamType === IncomeStreamType.WorkNumber
          ? this.parseTwnDetail(d)
          : this.parseDocDetail(d);
        const sopChecks: SopCheckResult[] = (parsed as any)?.sopChecks || [];
        for (const check of sopChecks) {
          if (!check.passed) {
            const icon = this.sopCheckIcon(check.checkName);
            flags.push({ severity: 'danger', icon, message: `${employer}: ${check.reason}` });
          }
        }
      }
    }

    return flags;
  }

  private parseDocDetail(d: AuditDraftDetail): DocUploadDetail | null {
    if (!d.detailJson) return null;
    try {
      return this.camelCaseKeys(JSON.parse(d.detailJson)) as DocUploadDetail;
    } catch {
      return null;
    }
  }

  private parseTwnDetail(d: AuditDraftDetail): WorkNumberDetail | null {
    if (!d.detailJson) return null;
    try {
      return this.camelCaseKeys(JSON.parse(d.detailJson)) as WorkNumberDetail;
    } catch {
      return null;
    }
  }

  private camelCaseKeys(obj: unknown): unknown {
    if (Array.isArray(obj)) return obj.map((i) => this.camelCaseKeys(i));
    if (obj && typeof obj === 'object' && obj.constructor === Object) {
      const result: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(obj)) {
        result[key.charAt(0).toLowerCase() + key.slice(1)] = this.camelCaseKeys(value);
      }
      return result;
    }
    return obj;
  }

  private sopCheckIcon(checkName: string): string {
    switch (checkName) {
      case 'DataFreshness': return '📅';
      case 'EmploymentStatus': return '🚫';
      case 'NameMatch': return '👤';
      case 'DocumentType': return '📄';
      case 'Authenticity': return '🔒';
      case 'PayPeriodSequence': return '📊';
      default: return '⚠️';
    }
  }
}
